import { useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { message as antdMessage, Modal } from "antd";
import { formatHistoryMessages } from "../utils/streamUtils";
import { ROUTE_PATHS } from "../router/RouterPaths";

/**
 * agentId 到 React Router 独立对话页的映射
 */
const AGENT_ID_ROUTE_MAP = {
    6: ROUTE_PATHS.ConversationKetogenicDiet,       // 生酮AI
    7: ROUTE_PATHS.ConversationRecipeCustomization,  // 食谱定制AI
    8: ROUTE_PATHS.ConversationSportNutrition,       // 运动营养AI
    9: ROUTE_PATHS.ConversationNutritionAssessment,  // 营养评估AI
  
};

// agentId 6/7/8/9 对应的 App.js 功能主页面（返回时 navigate(-1) 后 path="*" 会渲染该页面）
const AGENT_ID_PAGE_MAP = {
    6: "ketogenic_diet",
    7: "recipe_customization",
    8: "sport_nutrition",
    9: "nutrition_assessment",
};

/**
 * 封装历史记录相关的所有状态、数据获取、选择、删除逻辑
 *
 * @param {Object} params
 * @param {Object}   params.handleNewChatRef       - useRef，App 层延后设置，避免定义顺序问题。删除当前会话时通过 .current() 调用
 * @param {Array}    params.mainMessages           - 主页消息列表，用于删除时判断被删会话是否为当前正在查看的会话
 * @param {Function} params.stopActiveTypeWriter   - 停止当前页面的打字机动画，选历史前先中断
 * @param {Function} params.scrollToBottom         - 滚动到底部，选完历史消息后使用
 * @param {Function} params.setMainMessages        - 设置主页消息列表（切到历史主页面时写入）
 * @param {Function} params.setAgentMessages       - 设置 AI 医生消息列表（切到历史 agent 页时写入）
 * @param {Function} params.setNutritionMessages   - 设置营养师消息列表（切到历史营养师页时写入）
 * @param {Function} params.goTo                   - 页面导航（压入返回栈后切换），用于进入对话页前记录来源页
 * @param {Function} params.setIsSidebarOpen       - 控制侧边栏开/关，选完历史后关闭
 * @param {Object}   params.mainSessionIdRef       - 主页 sessionId 的 ref，选历史时赋值
 * @param {Object}   params.agentSessionIdRef      - AI 医生页 sessionId 的 ref
 * @param {Object}   params.nutritionSessionIdRef  - 营养师页 sessionId 的 ref
 */
export const useHistory = ({
    handleNewChatRef,
    mainMessages,
    stopActiveTypeWriter,
    scrollToBottom,
    setMainMessages,
    setAgentMessages,
    setNutritionMessages,
    goTo,
    setIsSidebarOpen,
    mainSessionIdRef,
    agentSessionIdRef,
    nutritionSessionIdRef,
}) => {
    const navigate = useNavigate();
    const [sidebarHistory, setSidebarHistory] = useState([]);
    const [isHisLoading, setIsHisLoading] = useState(false);
    const [hadHisList, setHadHisList] = useState(3);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMoreHistory, setHasMoreHistory] = useState(true);

    // 分页游标用 ref，避免 useCallback 依赖频繁变化
    const lastHistoryIdRef = useRef(null);
    const lastUserQueryTimeRef = useRef(undefined);
    const isFetchingMoreRef = useRef(false);

    // ---------------- 获取历史列表 ----------------
    const fetchHistoryList = useCallback(async (isLoadMore = false) => {
        if (isLoadMore && (!hasMoreHistory || isFetchingMoreRef.current)) return;

        if (isLoadMore) {
            isFetchingMoreRef.current = true;
            setIsFetchingMore(true);
        } else {
            lastHistoryIdRef.current = null;
            lastUserQueryTimeRef.current = undefined;
            setHasMoreHistory(true);
        }

        const uid = localStorage.getItem("medicalUserId");

        try {
            const payload = {
                userId: uid,
                pageSize: 10,
                id: isLoadMore ? lastHistoryIdRef.current : undefined,
                userQueryTime: isLoadMore ? lastUserQueryTimeRef.current : undefined,
            };

            const { data } = await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/userSessionList`,
                payload,
            );

            if (data.code === 200) {
                const newData = data.data || {};

                setSidebarHistory((prev) => {
                    if (!isLoadMore) return newData;

                    const merged = { ...prev };
                    const existingIds = new Set(
                        Object.values(prev)
                            .flat()
                            .map((item) => item.id),
                    );
                    Object.keys(newData).forEach((key) => {
                        const uniqueNewItems = newData[key].filter(
                            (item) => !existingIds.has(item.id),
                        );
                        if (uniqueNewItems.length > 0) {
                            merged[key] = merged[key]
                                ? [...merged[key], ...uniqueNewItems]
                                : uniqueNewItems;
                        }
                    });
                    return merged;
                });

                const allItems = Object.values(newData).flat();
                if (allItems.length > 0) {
                    const lastItem = allItems[allItems.length - 1];
                    lastHistoryIdRef.current = lastItem.id;
                    lastUserQueryTimeRef.current = lastItem.userQueryTime;
                    setHasMoreHistory(allItems.length >= 10);
                } else {
                    setHasMoreHistory(false);
                }
                setHadHisList(1);
            }
        } catch (err) {
            console.error("加载历史列表失败:", err);
        } finally {
            isFetchingMoreRef.current = false;
            setIsFetchingMore(false);
        }
    }, [hasMoreHistory]);

    // ---------------- 删除历史会话 ----------------
    const handleDeleteSession = (historyItem) => {
        Modal.confirm({
            title: "确认删除",
            content: `确定要删除这段对话吗？`,
            okText: "删除",
            okType: "danger",
            cancelText: "取消",
            centered: true,
            onOk: async () => {
                try {
                    const { data } = await axios.post(
                        `${window.PUBLIC_URL}/medagent/robot/deleteSession`,
                        { sessionId: historyItem.sessionId },
                    );
                    if (data.code === 200) {
                        antdMessage.success("删除成功");
                        fetchHistoryList(false);
                        if (
                            mainMessages.length > 0 &&
                            mainMessages[0].sessionId === historyItem.sessionId
                        ) {
                            handleNewChatRef.current?.();
                        }
                    } else {
                        antdMessage.error(data.msg || "删除失败");
                    }
                } catch (err) {
                    antdMessage.error("网络异常，删除失败");
                }
            },
        });
    };

    // ---------------- 选择历史会话 ----------------
    const handleSelectHistory = async (historyItem) => {
        stopActiveTypeWriter();

        // agentId 6/7/8/9 → 独立 React Router 对话页（页面内部自行加载历史）
        const agentIdNum = Number(historyItem.agentId);
        if (AGENT_ID_ROUTE_MAP[agentIdNum]) {
            setIsSidebarOpen(false);
            // 通过返回栈记录打开历史前所在的页面，保证会话内「查看」跳转的功能页返回时能回到正确页面
            // （例如从首页打开侧边栏，返回时应回到首页；从广场打开则回到广场）
            // 先切到对应的功能主页面，这样对话页返回时 navigate(-1) 能回到该功能页，而不是首页
            goTo(AGENT_ID_PAGE_MAP[agentIdNum]);
            navigate(AGENT_ID_ROUTE_MAP[agentIdNum], {
                state: {
                    sessionId: historyItem.sessionId,
                    healthDisease: historyItem.healthDisease||'',
                    date: historyItem.recordDate||'',
                    fromHistory: true,
                },
            });
            return;
        }

        setIsHisLoading(true);
        const uid = localStorage.getItem("medicalUserId");

        let targetPage = "main";
        if (historyItem.agentId === "1") targetPage = "doctor_xu";
        if (historyItem.agentId === "2") targetPage = "nutritionist";

        if (targetPage === "doctor_xu") {
            agentSessionIdRef.current = historyItem.sessionId;
        } else if (targetPage === "nutritionist") {
            nutritionSessionIdRef.current = historyItem.sessionId;
        } else {
            mainSessionIdRef.current = historyItem.sessionId;
        }

        try {
            const { data } = await axios.post(
                `${window.PUBLIC_URL}/medagent/robot/getChatHisList`,
                {
                    common: { channel: "wt", userId: uid },
                    sessionId: historyItem.sessionId,
                },
            );

            if (data.code === 200) {
                const historyContent = data.data?.content || [];
                const formattedMsgs = formatHistoryMessages(historyContent, historyItem.sessionId);

                // 写入对应页面的消息列表
                if (targetPage === "doctor_xu") {
                    setAgentMessages(formattedMsgs);
                } else if (targetPage === "nutritionist") {
                    setNutritionMessages(formattedMsgs);
                } else {
                    setMainMessages(formattedMsgs);
                }
                // 从历史记录进入对应页面：来源固定为首页，返回时回到首页
                goTo(targetPage, { from: "main" });
                setIsSidebarOpen(false);
                setTimeout(() => scrollToBottom(true), 200);
            }
        } catch (err) {
            antdMessage.error("获取详情失败");
        } finally {
            setIsHisLoading(false);
        }
    };

    return {
        sidebarHistory,
        setSidebarHistory,
        hadHisList,
        isHisLoading,
        isFetchingMore,
        hasMoreHistory,
        fetchHistoryList,
        handleSelectHistory,
        handleDeleteSession,
    };
};
